import {
  ApiGetHook,
  ApiGetHookOptions,
  ApiMutateHook,
  useApiGet,
  useApiMutate,
} from "@skanska/vsa-client-auth";
import { CacheKeys } from "../constants/CacheKeys";
import { ApiEndpoints } from "../models/ApiEndpoints";
import { ApiMethods } from "../models/ApiMethods";
import { ViewDefinition } from "../models/Field";
import {
  ActivateActivityRequest,
  Activity,
  CreateActivityRequest,
  MassCreateActivityDraftRequest,
  MassCreateActivityDraftResponse,
  MassPublishActivityRequest,
  MassPublishActivityResponse,
  PublishActivityRequest,
  SaveAndPublishActivityRequest,
  UpdateActivitiesRequest,
  UpdateActivityRequest,
} from "./../models/Activity";

export function useGetPublishedActivity(
  versionId: string
): ApiGetHook<Activity> {
  return useApiGet<Activity>(ApiEndpoints.Activities, versionId, [
    CacheKeys.Activity.SingleActivity,
    { versionId: versionId },
  ]);
}

export function useGetCurrentPublishedActivity(
  activityId: string
): ApiGetHook<Activity> {
  return useApiGet<Activity>(ApiEndpoints.ActivitiesCurrent, activityId, [
    CacheKeys.Activity.SingleActivity,
    { activityId: activityId },
  ]);
}

export function useDoesActivityHaveDraft(
  activityId: string,
  options?: ApiGetHookOptions
): ApiGetHook<string | null> {
  return useApiGet<string | null>(
    `${ApiEndpoints.ActivitiesCurrent}/${activityId}/hasdraft`,
    null,
    [CacheKeys.Activity.ActivityHasDraft, { activityId: activityId }],
    options
  );
}

export function useGetCurrentActivityDraft(
  activityId?: string
): ApiGetHook<Activity> {
  const draftVersion = useDoesActivityHaveDraft(activityId ?? "", {
    enabled: !!activityId,
  });

  const draft = useApiGet<Activity>(
    ApiEndpoints.ActivityDrafts,
    draftVersion.data,
    [CacheKeys.Activity.ActivityDraft, { versionId: draftVersion.data }],
    {
      enabled: !!draftVersion.data,
    }
  );

  return {
    data: draft.data,
    isLoading: draftVersion.isLoading || draft.isLoading,
    error: draftVersion.error ?? draft.error,
    isRefetching: draftVersion.isRefetching || draft.isRefetching,
  };
}

export function useGetActivityDraft(versionId: string): ApiGetHook<Activity> {
  return useApiGet<Activity>(ApiEndpoints.ActivityDrafts, versionId, [
    CacheKeys.Activity.ActivityDraft,
    { versionId: versionId },
  ]);
}

export function useGetActivityHistory(
  activityId: string
): ApiGetHook<Activity[]> {
  return useApiGet<Activity[]>(ApiEndpoints.ActivityFullHistory, activityId, [
    CacheKeys.Activity.ActivityHistory,
    { activityId: activityId },
  ]);
}

export function useGetActivityFields(): ApiGetHook<ViewDefinition> {
  return useApiGet<ViewDefinition>(ApiEndpoints.ActivityFields, null, [
    CacheKeys.Activity.ActivityFields,
  ]);
}

export function useGetActivityQuickEditFields(): ApiGetHook<ViewDefinition> {
  return useApiGet<ViewDefinition>(ApiEndpoints.ActivityQuickEditFields, null, [
    CacheKeys.Activity.ActivityFields,
    "quick-edit",
  ]);
}

export function useCreateActivityDraft(): ApiMutateHook<
  Activity,
  CreateActivityRequest
> {
  return useApiMutate<Activity, CreateActivityRequest>(
    ApiEndpoints.ActivityDrafts,
    ApiMethods.Post,
    null,
    null,
    cacheKeysToRefreshOnActivityChange
  );
}

export function useUpdateActivityDrafts(): ApiMutateHook<
  Activity,
  UpdateActivitiesRequest
> {
  return useApiMutate<Activity, UpdateActivitiesRequest>(
    ApiEndpoints.ActivityDrafts,
    ApiMethods.Patch,
    null,
    null,
    cacheKeysToRefreshOnActivityChange
  );
}

export function useUpdateActivityDraft(
  versionId: string
): ApiMutateHook<Activity, UpdateActivityRequest> {
  return useApiMutate<Activity, UpdateActivityRequest>(
    ApiEndpoints.ActivityDrafts,
    ApiMethods.Patch,
    versionId,
    null,
    cacheKeysToRefreshOnActivityChange
  );
}

export function useDeleteActivityDraft(
  versionId: string
): ApiMutateHook<void, void> {
  return useApiMutate<void, void>(
    ApiEndpoints.ActivityDrafts,
    ApiMethods.Delete,
    versionId,
    null,
    [],
    cacheKeysToRefreshOnActivityChange
  );
}

export function useCancelActivityDraft(
  versionId: string
): ApiMutateHook<void, void> {
  return useApiMutate<void, void>(
    `${ApiEndpoints.ActivityDrafts}/${versionId}/cancel`,
    ApiMethods.Delete,
    undefined,
    null,
    [],
    cacheKeysToRefreshOnActivityChange
  );
}

export function usePublishActivityDraft(): ApiMutateHook<
  Activity,
  PublishActivityRequest
> {
  return useApiMutate<Activity, PublishActivityRequest>(
    ApiEndpoints.ActivityPublish,
    ApiMethods.Post,
    null,
    null,
    [],
    cacheKeysToRefreshOnActivityChange
  );
}

export function useSaveAndPublishActivityDraft(): ApiMutateHook<
  Activity,
  SaveAndPublishActivityRequest
> {
  return useApiMutate<Activity, SaveAndPublishActivityRequest>(
    ApiEndpoints.ActivityDraftsSaveAndPublish,
    ApiMethods.Post,
    null,
    null,
    [],
    cacheKeysToRefreshOnActivityChange
  );
}

export function useMassPublishActivityDrafts(): ApiMutateHook<
  MassPublishActivityResponse,
  MassPublishActivityRequest
> {
  return useApiMutate<MassPublishActivityResponse, MassPublishActivityRequest>(
    ApiEndpoints.ActivityMassPublish,
    ApiMethods.Post,
    null,
    null,
    [],
    cacheKeysToRefreshOnActivityChange
  );
}

export function useMassCreateActivityDrafts(): ApiMutateHook<
  MassCreateActivityDraftResponse,
  MassCreateActivityDraftRequest
> {
  return useApiMutate<
    MassCreateActivityDraftResponse,
    MassCreateActivityDraftRequest
  >(
    ApiEndpoints.ActivityMassCreateDraft,
    ApiMethods.Post,
    null,
    null,
    [],
    cacheKeysToRefreshOnActivityChange
  );
}

export function useActivateActivity(): ApiMutateHook<
  Activity,
  ActivateActivityRequest
> {
  return useApiMutate<Activity, ActivateActivityRequest>(
    ApiEndpoints.ActivityActivate,
    ApiMethods.Post,
    null,
    null,
    cacheKeysToRefreshOnActivityChange
  );
}

export function useGetUsedPaths(activityId?: string): ApiGetHook<string[]> {
  return useApiGet<string[]>(
    ApiEndpoints.ActivityUsedPaths,
    activityId,
    CacheKeys.Activity.UsedPaths
  );
}

const cacheKeysToRefreshOnActivityChange: string[] = [
  CacheKeys.Activity.ActivityDraft,
  CacheKeys.Activity.ActivityHasDraft,
  CacheKeys.Activity.ActivityHistory,
  CacheKeys.Activity.ActivityList,
  CacheKeys.Activity.ActivityArchivedList,
  CacheKeys.Activity.SingleActivity,
  CacheKeys.Activity.UsedPaths,
  CacheKeys.Activity.ActivityDraftList,
  ApiEndpoints.ToolsCurrent,
  CacheKeys.Tool.ToolDraftList,
  CacheKeys.Document.DocumentDraftList,
  CacheKeys.Document.DocumentArchivedList,
  CacheKeys.Link.LinkDraftList,
  CacheKeys.Link.LinkArchivedList,
  ApiEndpoints.ToolsCurrentBatch,
  ApiEndpoints.ToolDocumentsCurrent,
  CacheKeys.Document.DocumentList,
  CacheKeys.Link.LinkList,
  CacheKeys.Image.SingleImage,
  ApiEndpoints.ImageCurrent,
  ApiEndpoints.ImageDrafts,
  CacheKeys.Image.ImageList,
  CacheKeys.Tool.ToolList,
] as const;
